/** Avatar + display name (users/{uid}.photoURL / displayName). */
import type { ReactNode } from 'react';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { cn } from '../lib/utils';

type Props = {
  displayName: string | null;
  photoURL: string | null;
  size?: 'sm' | 'lg';
  className?: string;
  /** Rendered under the name, aligned with the text column (e.g. sign out). */
  action?: ReactNode;
};

function initials(name: string | null): string {
  if (!name) return 'X9';
  const parts = name.trim().split(/\s+/);
  const first = parts[0]?.[0] || '';
  const last = parts.length > 1 ? parts[parts.length - 1][0] : '';
  return (first + last).toUpperCase() || 'X9';
}

export default function UserChip({
  displayName,
  photoURL,
  size = 'sm',
  className,
  action,
}: Props) {
  return (
    <div
      className={cn('flex min-w-0 gap-3', action ? 'items-start' : 'items-center', className)}
    >
      <Avatar className={size === 'lg' ? 'size-14 text-lg' : 'size-9 text-xs'}>
        {photoURL ? (
          <AvatarImage
            src={photoURL}
            alt={displayName || 'Avatar'}
            referrerPolicy="no-referrer"
          />
        ) : null}
        <AvatarFallback className="font-semibold">{initials(displayName)}</AvatarFallback>
      </Avatar>
      <div className="min-w-0 flex-1 group-data-[collapsible=icon]:hidden">
        {displayName && (
          <span
            className={cn(
              'block truncate font-semibold text-foreground',
              size === 'lg' ? 'text-lg' : 'text-sm',
            )}
          >
            {displayName}
          </span>
        )}
        {action}
      </div>
    </div>
  );
}
